import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Lock, ShieldCheck, Scale, CheckCircle2, IndianRupee } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { enrollAdvocate, fetchPaymentConfig, fetchEnrollmentConfig, verifyOrder, reconcileOrder } from '../lib/api';

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  city: '',
  specialty: '',
  experience: '',
  languages: '',
  fee: '\u20B910',
  bio: '',
};

const REQUIRED = ['name', 'email', 'phone', 'city', 'specialty', 'experience', 'languages', 'fee', 'bio'];

const openRazorpay = (orderRes, navigate) => {
  if (!window.Razorpay) {
    toast.error('Payment library did not load. Please refresh the page.');
    return;
  }
  const options = {
    key: orderRes.razorpay_key_id,
    amount: orderRes.amount_paise,
    currency: orderRes.currency,
    order_id: orderRes.razorpay_order_id,
    name: 'LawForLayman',
    description: orderRes.item_name,
    prefill: {
      name: orderRes.customer.name,
      email: orderRes.customer.email,
      contact: orderRes.customer.phone,
    },
    theme: { color: '#E89530' },
    handler: async (res) => {
      try {
        await verifyOrder({
          razorpay_order_id: res.razorpay_order_id,
          razorpay_payment_id: res.razorpay_payment_id,
          razorpay_signature: res.razorpay_signature,
        });
        navigate(`/enroll-advocate/success/${orderRes.order_id}`);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Payment verification failed. Please contact support.');
      }
    },
    modal: {
      ondismiss: async () => {
        try {
          const res = await reconcileOrder(orderRes.order_id);
          if (res.status === 'paid') {
            navigate(`/enroll-advocate/success/${orderRes.order_id}`);
            return;
          }
        } catch (_e) {
          // fall through
        }
        toast('Payment cancelled', {
          description: 'You can retry the ₹99 platform fee anytime. Your application will only proceed once payment is confirmed.',
        });
      },
    },
  };
  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', (resp) => {
    const desc = resp?.error?.description || 'Payment failed. Please try again.';
    toast.error(desc);
  });
  rzp.open();
};

const EnrollAdvocate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(true);
  const [feeInr, setFeeInr] = useState(99);
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchPaymentConfig().catch(() => ({ enabled: false })),
      fetchEnrollmentConfig().catch(() => ({ fee_inr: 99 })),
    ]).then(([pc, ec]) => {
      setPaymentEnabled(Boolean(pc?.enabled));
      setFeeInr(Number(ec?.fee_inr) || 99);
    });
  }, []);

  const handleChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = REQUIRED.filter((k) => !String(form[k] || '').trim());
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(', ')}`);
      return;
    }
    if (form.bio.trim().length < 20) {
      toast.error('Please write a bio of at least 20 characters so citizens can understand your practice.');
      return;
    }
    if (!agree) {
      toast.error('Please agree to the verification and listing terms.');
      return;
    }
    if (!paymentEnabled) {
      toast.error('Online payments are not yet configured. Please contact support.');
      return;
    }
    setSubmitting(true);
    try {
      const orderRes = await enrollAdvocate({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        specialty: form.specialty.trim(),
        experience: form.experience.trim(),
        languages: form.languages.trim(),
        fee: form.fee.trim(),
        bio: form.bio.trim(),
      });
      openRazorpay(orderRes, navigate);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start enrollment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="enroll-advocate-page">
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">ENROLL WITH LAWFORLAYMAN</p>
          <h1 className="font-serif-besley text-4xl md:text-5xl mb-4">Get discovered by citizens who need you</h1>
          <p className="text-[#C9BFA8] max-w-2xl leading-relaxed">
            Join a verified network of advocates helping India understand its rights. Pay a one-time platform
            listing fee of ₹{feeInr}, complete a short profile, and our team will review your application within
            3&ndash;5 working days.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-14">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white border border-[#E8DCC0] rounded-2xl p-8 space-y-6" data-testid="enroll-advocate-form">
              <div>
                <h2 className="font-serif-besley text-2xl text-[#0F1B2D]">Your profile</h2>
                <p className="text-sm text-[#5a5a5a] mt-1">All fields are required. This will appear on your public directory listing once approved.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-[#0F1B2D]">Full name *</Label>
                  <Input data-testid="enroll-name" value={form.name} onChange={handleChange('name')} className="mt-2 h-11 border-[#E8DCC0]" placeholder="Adv. Full Name" />
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Phone *</Label>
                  <Input data-testid="enroll-phone" value={form.phone} onChange={handleChange('phone')} className="mt-2 h-11 border-[#E8DCC0]" placeholder="10-digit mobile" />
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Email *</Label>
                  <Input data-testid="enroll-email" type="email" value={form.email} onChange={handleChange('email')} className="mt-2 h-11 border-[#E8DCC0]" placeholder="you@example.com" />
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">City *</Label>
                  <Input data-testid="enroll-city" value={form.city} onChange={handleChange('city')} className="mt-2 h-11 border-[#E8DCC0]" placeholder="Chennai" />
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Primary specialty *</Label>
                  <Input data-testid="enroll-specialty" value={form.specialty} onChange={handleChange('specialty')} className="mt-2 h-11 border-[#E8DCC0]" placeholder="Family & Matrimonial" />
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Experience *</Label>
                  <Input data-testid="enroll-experience" value={form.experience} onChange={handleChange('experience')} className="mt-2 h-11 border-[#E8DCC0]" placeholder="e.g. 8 yrs" />
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Languages you practise in *</Label>
                  <Input data-testid="enroll-languages" value={form.languages} onChange={handleChange('languages')} className="mt-2 h-11 border-[#E8DCC0]" placeholder="Hindi, English, Tamil" />
                  <p className="text-xs text-[#5a5a5a] mt-1.5">Separate multiple languages with a comma.</p>
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Consultation fee</Label>
                  <Input data-testid="enroll-fee" value={form.fee} readOnly disabled className="mt-2 h-11 border-[#E8DCC0] bg-[#EDE3CC] cursor-not-allowed" />
                  <p className="text-xs text-[#5a5a5a] mt-1.5">All LawForLayman advocates offer a standard ₹10 introductory consultation.</p>
                </div>
              </div>

              <div>
                <Label className="text-[#0F1B2D]">Short bio *</Label>
                <Textarea data-testid="enroll-bio" rows={5} value={form.bio} onChange={handleChange('bio')} className="mt-2 border-[#E8DCC0]" placeholder="Describe your practice, notable matters and how you help citizens (at least 20 characters)." />
                <p className="text-xs text-[#5a5a5a] mt-1.5">{form.bio.length} / 20 characters minimum</p>
              </div>

              <label className="flex items-start gap-3 text-sm text-[#3a3a3a] leading-relaxed">
                <input
                  data-testid="enroll-agree"
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-1 accent-[#E89530]"
                />
                <span>
                  I confirm that I am a registered advocate in India and consent to LawForLayman verifying my
                  Bar Council enrolment. I understand the ₹{feeInr} fee is non-refundable and that listing is
                  subject to admin approval.
                </span>
              </label>

              {!paymentEnabled && (
                <div className="p-4 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  Online payments are not yet configured. Please contact <a className="underline" href="mailto:contact@lawforlayman.com">contact@lawforlayman.com</a>.
                </div>
              )}

              <button
                data-testid="enroll-submit-btn"
                type="submit"
                disabled={submitting || !paymentEnabled}
                className="btn-orange w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-medium disabled:opacity-70"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {submitting ? 'Preparing…' : `Pay ₹${feeInr} platform fee & submit`}
              </button>
              <p className="text-xs text-[#5a5a5a] flex items-center gap-2 justify-center">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure INR payments via Razorpay.
              </p>
            </form>
          </div>

          <aside className="lg:col-span-2">
            <div className="bg-[#EDE3CC] border border-[#D6CAB0] rounded-2xl p-8 sticky top-6" data-testid="enroll-summary">
              <p className="text-xs tracking-[0.25em] text-[#A36B1F] mb-3">HOW IT WORKS</p>
              <ol className="space-y-4 text-sm text-[#0F1B2D]">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#0F1B2D] text-[#F8F2E4] flex items-center justify-center text-xs font-medium shrink-0">1</span>
                  <span>Fill your professional details and submit the form.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#0F1B2D] text-[#F8F2E4] flex items-center justify-center text-xs font-medium shrink-0">2</span>
                  <span>Pay a one-time ₹{feeInr} platform listing fee via Razorpay (UPI, cards, netbanking).</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#0F1B2D] text-[#F8F2E4] flex items-center justify-center text-xs font-medium shrink-0">3</span>
                  <span>Our verification team reviews your Bar Council enrolment within 3&ndash;5 working days.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#0F1B2D] text-[#F8F2E4] flex items-center justify-center text-xs font-medium shrink-0">4</span>
                  <span>Approved profiles go live on the public advocate directory instantly.</span>
                </li>
              </ol>

              <div className="border-t border-[#D6CAB0] mt-6 pt-6 space-y-3 text-sm">
                <div className="flex items-start gap-2 text-[#3a3a3a]">
                  <Scale className="w-4 h-4 mt-0.5 text-[#A36B1F] shrink-0" />
                  <span>Only Bar Council-registered advocates are eligible.</span>
                </div>
                <div className="flex items-start gap-2 text-[#3a3a3a]">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#A36B1F] shrink-0" />
                  <span>Direct contact details are never shown publicly &mdash; enquiries are routed via our team.</span>
                </div>
              </div>

              <div className="border-t border-[#D6CAB0] mt-6 pt-6 flex items-center justify-between">
                <span className="text-[#5a5a5a] text-sm">Platform listing fee</span>
                <span className="font-serif-besley text-3xl text-[#0F1B2D] inline-flex items-center" data-testid="enroll-fee-amount">
                  <IndianRupee className="w-5 h-5 mr-0.5" />{feeInr}
                </span>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default EnrollAdvocate;
